// --- the earlier scene a frame contradicts --------------------------------
//
// A judge reading a scene can find that what it is looking at is right and an
// EARLIER scene is what is wrong ("the lantern is lit here, so it must already
// be in her hand two scenes ago"). The engine records that on the verdict as
// `metadata.cross_scene`, and then stamps what it did about it:
//
//   cross_scene_applied   — it regenerated the target itself.
//   cross_scene_suggested — it did not (permission off, the per-run cap spent,
//                           or its own loop guard refused), and the reason.
//
// Neither stamp means the engine has not ruled on it yet. The proposal is
// shown either way: Dorian's rule is that the operator can always apply it by
// hand, which is what the panel's "Fix scene N" button is for.

import { ReviewVerdict } from '../../../api/structs';
import { formatSceneLabel } from '../../clips/sceneLabel';

/** What `metadata.cross_scene` and its two stamps say, read as one answer. */
export interface CrossSceneSuggestion {
  /** The scene to fix, in the run's own id vocabulary. */
  sceneId: string;
  /** What the engine proposes doing to it — "regenerate" today. */
  action: string;
  /** The corrective note: what that scene must show instead. */
  what: string;
  /** Why this frame says so. */
  why: string;
  /** The engine's own record of the fix it already made. */
  applied: { sceneId: string; targetRunId: string } | null;
  /** Whether the engine deliberately left the fix to the operator. */
  suggested: boolean;
  /** Why it did — '' when the stamp named no reason. */
  suggestedReason: string;
}

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

const asText = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');

/**
 * A `cross_scene_suggested` stamp is the bare reason string; tolerate an object
 * carrying it under `reason`, exactly as the video-prompt stamps are read.
 */
const suggestedReason = (value: unknown): string =>
  typeof value === 'string' ? value.trim() : asText(asRecord(value)?.reason);

/**
 * The scene NUMBER the thread shows, for the sentences that already say the
 * word "scene" ("Fix scene 3"). Routed through `formatSceneLabel` so the three
 * id vocabularies stay one; an id it cannot number is its own label.
 */
export const crossSceneLabel = (sceneId: string): string => {
  if (!sceneId.trim()) return '';
  const label = formatSceneLabel(sceneId);
  return label.startsWith('Scene ') ? label.slice('Scene '.length) : sceneId;
};

/**
 * What one verdict says about an earlier scene, or null when it says nothing.
 *
 * The applied stamp stands on its own: it is the record of an action, and the
 * proposal that produced it is not re-stamped on every later verdict.
 */
export const readCrossScene = (verdict?: ReviewVerdict | null): CrossSceneSuggestion | null => {
  const metadata = verdict?.metadata || {};
  const proposal = asRecord(metadata.cross_scene);
  const appliedStamp = asRecord(metadata.cross_scene_applied);

  const applied = appliedStamp
    ? {
        sceneId: asText(appliedStamp.scene_id),
        targetRunId: asText(appliedStamp.target_run_id),
      }
    : null;

  const sceneId = asText(proposal?.scene_id) || applied?.sceneId || '';
  if (!sceneId) return null;

  const reason = suggestedReason(metadata.cross_scene_suggested);
  return {
    sceneId,
    action: asText(proposal?.action),
    what: asText(proposal?.what),
    why: asText(proposal?.why),
    applied,
    suggested: metadata.cross_scene_suggested !== undefined,
    suggestedReason: reason,
  };
};
